import { getStorage, setTokens, clearTokens, isTokenExpired } from './storage'
import { AUTH_BASE_URL, API_BASE_URL } from './constants'

// Fired when the refresh token is dead — listeners navigate to PairingScreen
type AuthExpiredListener = () => void
const authExpiredListeners: AuthExpiredListener[] = []

export function onAuthExpired(fn: AuthExpiredListener): () => void {
  authExpiredListeners.push(fn)
  return () => {
    const idx = authExpiredListeners.indexOf(fn)
    if (idx !== -1) authExpiredListeners.splice(idx, 1)
  }
}

function notifyAuthExpired() {
  for (const fn of authExpiredListeners) fn()
}

// In-flight refresh promise — prevents duplicate refreshes when multiple calls fail at once
let refreshPromise: Promise<boolean> | null = null

export async function refreshAccessToken(): Promise<boolean> {
  if (refreshPromise) return refreshPromise

  refreshPromise = (async () => {
    const storage = getStorage()
    if (!storage.refreshToken) return false

    try {
      const res = await fetch(`${AUTH_BASE_URL}/extension-refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ refreshToken: storage.refreshToken }),
      })

      if (!res.ok) {
        clearTokens()
        notifyAuthExpired()
        return false
      }

      const data = await res.json() as { accessToken: string; expiresIn: number }
      const tokenExpiresAt = Date.now() + (data.expiresIn ?? 900) * 1000
      setTokens({ accessToken: data.accessToken, tokenExpiresAt })
      return true
    } catch (err) {
      console.error('[planner-outlook] token refresh failed:', err)
      return false
    } finally {
      refreshPromise = null
    }
  })()

  return refreshPromise
}

// ── Core request ──────────────────────────────────────────────────────────────

async function request<T>(
  path: string,
  options: RequestInit = {},
  retried = false
): Promise<{ data: T; error: null; status: number } | { data: null; error: string; status: number }> {
  const storage = getStorage()

  let authHeaders: Record<string, string> = {}

  if (storage.accessToken) {
    if (isTokenExpired(storage.tokenExpiresAt)) {
      const refreshed = await refreshAccessToken()
      if (!refreshed) {
        return { data: null, error: 'Session expired. Please re-pair the add-in.', status: 401 }
      }
      const updated = getStorage()
      if (updated.accessToken) {
        authHeaders = { Authorization: `Bearer ${updated.accessToken}` }
      }
    } else {
      authHeaders = { Authorization: `Bearer ${storage.accessToken}` }
    }
  } else {
    return { data: null, error: 'Not authenticated. Please pair the add-in.', status: 401 }
  }

  try {
    const res = await fetch(`${API_BASE_URL}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        ...authHeaders,
        ...options.headers,
      },
    })

    if (res.status === 401 && !retried && storage.accessToken) {
      const refreshed = await refreshAccessToken()
      if (refreshed) return request<T>(path, options, true)
      return { data: null, error: 'Session expired. Please re-pair the add-in.', status: 401 }
    }

    if (!res.ok) {
      const body = await res.json().catch(() => ({})) as { message?: string; code?: string }
      const message = body.message ?? `HTTP ${res.status}`
      return { data: null, error: message, status: res.status }
    }

    const data = await res.json() as T
    return { data, error: null, status: res.status }
  } catch (err) {
    return { data: null, error: err instanceof Error ? err.message : 'Network error', status: 0 }
  }
}

// ── Pairing ───────────────────────────────────────────────────────────────────

export async function pairWithCode(
  code: string,
  name?: string
): Promise<
  | { success: true; orgName: string; userName: string | null }
  | { success: false; error: string }
> {
  try {
    const res = await fetch(`${AUTH_BASE_URL}/extension-pair`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({
        code,
        name: name ?? 'Athenty Outlook Add-in',
        channel: 'outlook',
      }),
    })

    if (!res.ok) {
      const body = await res.json().catch(() => ({})) as { message?: string }
      return { success: false, error: body.message ?? `HTTP ${res.status}` }
    }

    const data = await res.json() as {
      accessToken: string
      refreshToken: string
      expiresIn: number
      user: { orgId: string; orgName: string; role: string; nameFirst: string | null; nameLast: string | null }
    }

    const tokenExpiresAt = Date.now() + (data.expiresIn ?? 900) * 1000
    setTokens({
      accessToken: data.accessToken,
      refreshToken: data.refreshToken,
      tokenExpiresAt,
      pairedOrg: { id: data.user.orgId, name: data.user.orgName },
      pairedUser: { role: data.user.role, nameFirst: data.user.nameFirst, nameLast: data.user.nameLast },
    })

    const userName = [data.user.nameFirst, data.user.nameLast].filter(Boolean).join(' ') || null
    return { success: true, orgName: data.user.orgName, userName }
  } catch (err) {
    return { success: false, error: err instanceof Error ? err.message : 'Network error' }
  }
}

// ── API calls ─────────────────────────────────────────────────────────────────

export function getMatters(params: Record<string, string> = {}) {
  const qs = new URLSearchParams(params).toString()
  return request<import('../types').PaginatedResult<import('../types').Matter>>(
    `/matters${qs ? `?${qs}` : ''}`
  )
}

export function linkEmail(
  matterId: string,
  body: {
    // Optional fields mirror the API Zod schema (.optional() = string | undefined, NOT null).
    // Callers must convert nulls to undefined — JSON.stringify omits undefined keys.
    // Declared as `string | undefined` (not just `string?`) to satisfy exactOptionalPropertyTypes.
    messageId?: string | undefined
    threadId?: string | undefined
    subject?: string | undefined
    fromAddress?: string | undefined
    fromName?: string | undefined
    toAddresses?: { email: string; name?: string | undefined }[]
    ccAddresses?: { email: string; name?: string | undefined }[]
    snippet?: string | undefined
    bodyText?: string | undefined
    bodyHtml?: string | undefined
    attachments?: { filename: string; mimeType: string; size: number }[]
    sentAt?: string | undefined
    source: 'extension'
    direction: 'inbound' | 'outbound'
    matterDocumentIds?: string[]
    alwaysAutoFile?: boolean
  }
) {
  return request<{ id: string }>(`/matters/${matterId}/emails`, {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

// ── #65 — matter-document upload (used by the email-attachment bridge) ──────

export interface EnsuredFolder {
  id: string
  matterId: string
  orgId: string
  parentId: string | null
  name: string
  path: string
}

export function ensureFolderPath(matterId: string, path: string) {
  return request<EnsuredFolder>(`/matters/${matterId}/folders/ensure-path`, {
    method: 'POST',
    body: JSON.stringify({ path }),
  })
}

export type InitiateUploadResult =
  | { mode: 'dedup'; key: string; contentHash: string }
  | { mode: 'simple'; key: string; putUrl: string }
  | {
      mode: 'multipart'
      key: string
      uploadId: string
      partUrls: string[]
      partSize: number
      partCount: number
    }

export function initiateUpload(
  matterId: string,
  body: {
    name: string
    contentHash: string
    size: number
    mimeType: string
    folderId?: string | null
  }
) {
  return request<InitiateUploadResult>(`/matters/${matterId}/documents/uploads`, {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

// ── #182 — email template render (compose signature injection) ───────────────

export function renderEmailTemplate(kind: 'signature' | 'footer') {
  return request<{ html: string }>(`/email-templates/render?kind=${encodeURIComponent(kind)}`)
}

export function completeUpload(
  matterId: string,
  body: {
    contentHash: string
    name: string
    size: number
    mimeType: string
    folderId?: string | null
    uploadId?: string
    partETags?: { PartNumber: number; ETag: string }[]
    sourceEmailId?: string | null
  }
) {
  return request<{ id: string; name: string; contentHash: string }>(
    `/matters/${matterId}/documents/uploads/complete`,
    {
      method: 'POST',
      body: JSON.stringify(body),
    }
  )
}
